import { Box, Flex, Tooltip } from '@fullstory/components';
import { PlayButton, ServerEventButton } from '@fullstory/overlays-ui';
import { EventMarker, SearchUser } from '@fullstory/proto/fs/services/alexa';
import { timestamp, Timestamp } from '@fullstory/proto/google/protobuf';
import { generateRoutePath, ORG_ROUTE, SessionExpanderRoute } from '@fullstory/routes';
import { classNames } from '@fullstory/util/fmt';
import { isOnline as getIsOnline } from '@fullstory/util/session';
import moment from 'moment';
import React, { useMemo } from 'react';
import { Link } from 'react-router-dom';
import { usePlaybackRetention, useStoryAiEnabled } from '../../../hooks';
import { PreviewSession } from '../../PreviewSession';
import css from './sessionInfo.module.scss';
import { SessionInfoDetailCopy } from './SessionInfoDetailCopy';
import { SessionInfoLinks } from './SessionInfoLinks';
import { SessionInfoUnavailable } from './SessionInfoUnavailable';

interface SessionInfoProps {
  completeSessions: boolean;
  eventMarkers: EventMarker[];
  isWatched: boolean;
  newTab?: boolean;
  now: Date;
  onPlayClick?: () => void;
  onUserInfoClick: () => void;
  orgId: string;
  sessionLink: SessionExpanderRoute;
  user: SearchUser;
  autoSelect?: boolean;
}

interface WithSessionPreviewProps {
  userId: string;
  sessionId: string;
  pageId: string;
  pageOffset: number;
  timestamp: timestamp;
  to: React.ComponentProps<typeof Link>['to'];
  onPlayClick?: () => void;
  newTab?: boolean;
  children: React.ReactNode;
}

const WithSessionPreview: React.FC<WithSessionPreviewProps> = ({
  userId,
  sessionId,
  pageId,
  pageOffset,
  timestamp,
  to,
  onPlayClick,
  newTab,
  children,
}) => {
  const { summariesEnabled } = useStoryAiEnabled();

  if (!summariesEnabled) {
    return children;
  }
  if (!userId || !sessionId || !pageId || !timestamp) {
    return children;
  }
  return (
    <Tooltip
      tooltipContent={
        <PreviewSession
          userId={userId!}
          sessionId={sessionId!}
          pageId={pageId!}
          pageOffset={pageOffset}
          timestamp={timestamp!}
          to={to}
          onPlayClick={onPlayClick}
          newTab={newTab}
        />
      }
      maxWidth={1000}
      disablePadding
      variant="light"
      showDelay={300}
    >
      {children}
    </Tooltip>
  );
};

const SessionInfo: React.FC<SessionInfoProps> = ({
  completeSessions,
  eventMarkers,
  isWatched,
  newTab,
  now: nowProp,
  onPlayClick,
  onUserInfoClick,
  orgId,
  sessionLink,
  user,
  autoSelect,
}) => {
  const now = moment(nowProp);
  const lastEventTime = moment(Timestamp.toMillis(user.lastMatchingEventStartAt));

  // TODO: Take into account clip searches. See User List v1:
  // https://github.com/cowpaths/mn/blob/cc8eb6b91041257b20cabb13228b5ec70cdd6c3f/projects/fullstory/ts/ui/segments/lists/cliplist.ts#L307
  const isOnline = getIsOnline(now, lastEventTime, completeSessions);
  let sessionLinkHref =
    sessionLink.route.length > 0
      ? `${generateRoutePath(ORG_ROUTE, { orgId })}/${sessionLink.route.join('/')}`
      : '';

  // To be used for deep linking to detection events.
  if (autoSelect) {
    sessionLinkHref = `${sessionLinkHref}?autoselect=true`;
  }
  const sessionLinkState = sessionLink.state;
  const numberOfSessionByEventMarkers = getNumSessions(eventMarkers);

  const { insideRetentionWindow, retentionWindowInMonths } = usePlaybackRetention(
    lastEventTime.toDate(),
  );

  const { summariesEnabled } = useStoryAiEnabled();

  const { userId, sessionId, pageId, pageOffset, timestamp } = useMemo(() => {
    if (!summariesEnabled || !user?.lastMatchingUserSessionPageId) {
      return { userId: '', sessionId: '', pageId: '', pageOffset: 0, timestamp: null };
    }
    const [userId, sessionId, pageId] = user.lastMatchingUserSessionPageId.split('!');
    const pageOffset = user.lastMatchingEventPageOffset;
    const timestamp = user.lastMatchingEventStartAt;
    return { userId, sessionId, pageId, pageOffset, timestamp };
  }, [user, summariesEnabled]);

  return (
    <Box
      data-component="SessionInfo"
      className={classNames('sessionInfo', css.sessionInfo)}
      paddingRight={16}
    >
      <Flex className={css.sessionInfoFlex}>
        <Box paddingRight={12}>
          {sessionLinkHref ? (
            <WithSessionPreview
              userId={userId!}
              sessionId={sessionId!}
              pageId={pageId!}
              pageOffset={pageOffset}
              timestamp={timestamp!}
              to={{
                pathname: sessionLinkHref,
                state: sessionLinkState,
              }}
              onPlayClick={onPlayClick}
              newTab={newTab}
            >
              <PlayButton
                isClip={
                  user.fetchClips &&
                  numberOfSessionByEventMarkers >
                    1 /* PlayButton should not consider the session a clip if there is only one actual session. */
                }
                isOnline={isOnline}
                isWatched={isWatched}
                onClick={onPlayClick}
                to={{
                  pathname: sessionLinkHref,
                  state: sessionLinkState,
                }}
                newTab={newTab}
                disabled={!insideRetentionWindow}
              />
            </WithSessionPreview>
          ) : (
            <ServerEventButton />
          )}
        </Box>

        <div className={classNames('sessionAndDateSummary', css.sessionInfo)}>
          {insideRetentionWindow ? (
            <>
              <SessionInfoLinks
                isOnline={isOnline}
                onUserInfoClick={onUserInfoClick}
                sessionLinkHref={sessionLinkHref}
                sessionLinkState={sessionLinkState}
                user={user}
              />
              <SessionInfoDetailCopy
                completeSessions={completeSessions}
                eventMarkers={eventMarkers}
                now={nowProp}
                numSessions={numberOfSessionByEventMarkers}
                sessionLinkHref={sessionLinkHref}
                user={user}
              />
            </>
          ) : (
            <SessionInfoUnavailable
              retentionWindowInMonths={retentionWindowInMonths}
              orgId={orgId}
            />
          )}
        </div>
      </Flex>
    </Box>
  );
};

function getNumSessions(eventMarkers: EventMarker[]) {
  return new Set(eventMarkers.filter(m => m.sessionId !== '0').map(m => m.sessionId)).size;
}

export default SessionInfo;
